import { TOCViewType } from '@components/Common/CommonEnums';
import { useAuditHistoryContext } from '@context/AuditHistoryContext';
import { useAurigaContext } from '@context/AurigaContext';
import { useDeckContext } from '@context/DeckContext';
import { useUserContext } from '@context/UserContext';
import { createId } from '@paralleldrive/cuid2';
import {
  DeckColumnDeleteItemsInput,
  DeckColumnItemsInput,
  ScheduleType,
} from '@services/api/deckManagement/graphql/schema/API';
import { useGetDeckColumnForDeck } from '@services/api/deckManagement/hooks';
import { useSaveDeck, UseSaveDeckParams } from '@services/api/deckManagement/hooks/useDeck';
import { useTOCEntries } from '@services/api/deckManagement/hooks/useTOCEntries';
import {
  checkDuplicateFieldEntry,
  createPageIdToTemplateMap,
  getItemIdxByInternalId,
  getNextSerialNumber,
  parseIdFromKey,
} from '@utils/deckHelpers';
import {
  changeEntryField,
  changeMultipleEntryFields,
  initializeMetadata,
  markEntryAsDeleted,
  prepareEntriesForSaveDeckOperation,
  transformFieldsInEntries,
} from '@utils/deckManagementEntityHelpers';
import { findIndex, forEach, isEmpty, isEqual, isNil, sortBy, uniqBy } from 'lodash';
import { createContext, ReactNode, use, useCallback, useEffect, useMemo, useState } from 'react';
import { ChangeSetTargets, SaveDeckChangeSetSchema, SaveDeckChangeSetType } from 'src/schema';
import { EnhancedTOCEntry, TOCContextProviderProps, TOCContextType } from 'types/TOCContext.types';

/**
 * TOC Context
 *
 * React context for TOC state management.
 */
export const TOCContext = createContext<TOCContextType>({
  tocEntries: [],
  currentView: 'active',
  isLoading: false,
  isError: false,
  error: null,
  hasInitialDataLoaded: false,
  setView: () => {},
  getTocItemIdx: () => {},
  getTocItemsByGroup: () => new Map<string, EnhancedTOCEntry[]>(),
  updateTocEntry: () => {},
  updateTocPageId: () => {},
  deletePage: () => {},
  togglePageVisibility: () => '',
  togglePageActive: () => {},
  moveToActiveWithScheduleType: () => {},
  copyPage: () => {},
  createNewPage: () => {},
  saveChanges: async () => {},
  discardChanges: () => {},
  hasUnsavedChanges: () => false,
  scheduleType: ScheduleType.APPROVED,
  setScheduleType: () => {},
  isSaving: false,
  deckColumnsForDeck: [],
  isGetDeckColumnForDeckLoading: false,
  pageIdToTemplateMap: new Map(),
  saveTemplate: async () => {},
});

/**
 * TOC Provider Component
 *
 * This component provides TOC context to the application.
 * It manages the state of TOC entries, and TOC-related operations.
 *
 * Key responsibilities:
 * - Fetch and manage TOC entries
 * - Track changes to TOC page names and page names
 * - Handle page deletion
 * - Manage page reordering
 * - Provide methods to save or discard changes
 *
 * @param {TOCContextProviderProps} props - The component props
 * @returns {JSX.Element} The provider component
 */
export const TOCContextProvider = ({ children }: TOCContextProviderProps): ReactNode => {
  const { username } = useUserContext();
  // Current Context selected DeckId
  const { currentDeckId: selectedDeckId, currentDeck: selectedDeck } = useDeckContext();
  // Auriga General Context for Toaster  and RumLogger access
  const { toastNotification, rumLogger } = useAurigaContext();
  // To capture Page Library edits in Audit History
  const { generatePageLibraryAuditTrail, saveAuditTrail } = useAuditHistoryContext();
  // State for current TOC view (active/inactive pages)
  const [currentView, setCurrentView] = useState<TOCViewType>('active');
  // Tracks first data load of TOC items
  const [hasInitialDataLoaded, setHasInitialDataLoaded] = useState(false); // To resolve seeing no TOC items loaded message before Api is done
  // Tracks schedule type dropdown selection. Defaults to Approved
  const [scheduleType, setScheduleType] = useState<ScheduleType>(ScheduleType.APPROVED);
  // To track saving state when a User clicks the SAVE button
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const {
    data: allTocEntriesData = [],
    isLoading: isTocEntriesLoading,
    isError: isTocEntriesError,
    isFetched: isTocEntriesFetched,
    error: tocEntriesError,
    refetch: refetchActive,
  } = useTOCEntries(selectedDeckId || '');

  const tocEntriesData = useMemo(() => {
    return allTocEntriesData || [];
  }, [allTocEntriesData]);

  const { data: deckColumnsForDeck, isLoading: isGetDeckColumnForDeckLoading } = useGetDeckColumnForDeck({
    deck_id: selectedDeckId || '',
    limit: 500,
  });

  const pageIdToTemplateMap = createPageIdToTemplateMap(deckColumnsForDeck || []);

  /**
   * State for tracking original and modified entries
   * - originalTocEntries: Holds the original TOC entries fetched from the API
   * - modifiedTocEntries: Holds the modified TOC entries as the user makes changes
   * - tocEntriesByGroup: Map for easy reference of All TOC items by Grouping ('active-approved', 'active-modified', 'inactive')
   */
  const [originalTocEntries, setOriginalTocEntries] = useState<EnhancedTOCEntry[]>([]);
  const [modifiedTocEntries, setModifiedTocEntries] = useState<EnhancedTOCEntry[]>([]);
  const [tocEntriesByGroup, setTocEntriesByGroup] = useState<Map<string, EnhancedTOCEntry[]>>(new Map());

  /**
   * Reset core State values for TOC as needed
   */
  const resetTocData = useCallback(() => {
    setOriginalTocEntries([]);
    setModifiedTocEntries([]);
    setTocEntriesByGroup(new Map());
  }, []);

  /**
   * Creates a new Enhanced TOC Entry in the Collection received
   * @param prevEntries - Collection to create the new EnhancedTOCEntry in
   * @param assignedDeckId - Deck ID to associate the new EnhancedTOCEntry with
   * @param tocPageName - TOC page name to assign the EnhancedTOCEntry (no uniqueness constraints)
   * @param pageName - Page name to assign the EnhancedTOCEntry (should be unique across all known items)
   */
  const createNewTOCEntry = useCallback(
    (
      prevEntries: EnhancedTOCEntry[],
      assignedDeckId: string,
      tocPageName?: string,
      pageName?: string,
    ): EnhancedTOCEntry => {
      const internalId = createId(); // Generate a unique cuid to be used for the new entry
      const currentDate = new Date().toISOString(); // Matches expected AWSDateTime

      // Get the next serial number using the helper function
      const nextSerialNo = getNextSerialNumber(prevEntries);
      // Generate the next unique Page Name suggestion based on all existing unique TOC Items
      const knownTocEntries = uniqBy([...originalTocEntries, ...modifiedTocEntries], 'toc_id');
      const existingPageNames = new Set(knownTocEntries.map((entry: EnhancedTOCEntry) => entry.page_name));
      // Get a simple sequence number across all known TOC Items and the nextSerialNo created above
      let nextPageSequenceNo = Math.max(nextSerialNo, knownTocEntries.length + 1);
      // Given our anticipated creation format, increment till we find the next unique Page Name suggestion
      while (existingPageNames.has(`Page Name ${nextPageSequenceNo}`)) {
        nextPageSequenceNo += 1;
      }

      const newPage = {
        __typename: 'TOCEntry',
        toc_id: internalId,
        pk: `DECK#${assignedDeckId}`, // Not explicitly needed, as this is a new entry. Not sent to any Api
        sk: `TOC#${internalId}#${nextSerialNo}`, // Not explicitly needed, as this is a new entry. Not sent to any Api
        page_id: createId(), // Generates a unique cuid for page_id that differs from the internalId used with toc_id
        page_no: `${nextSerialNo}`,
        toc_page_name: tocPageName !== undefined ? tocPageName : `TOC Page Name (${nextPageSequenceNo})`,
        page_name: pageName !== undefined ? pageName : `Page Name ${nextPageSequenceNo}`,
        is_visible: true,
        is_active: false,
        group_name: '',
        serial_no: nextSerialNo,
        created_date: currentDate,
        last_updated_date: currentDate,
        schedule_type: null, // Set Null on New Page Creation from Inactive pages
      };

      return initializeMetadata(newPage as EnhancedTOCEntry, {
        internalId, // For new TOC items (not yet saved), align metadata.internalId with toc_id for consistent lookups
        isNewRow: true,
        sortPos: nextSerialNo,
      });
    },
    [modifiedTocEntries, originalTocEntries],
  );

  /**
   * Get the index of a TOC item in the currently available list of TOC entries.
   *
   * @param currTocEntries - The current list of TOC entries to search
   * @param {string} lookupPk - The lookup partition key of the TOC item
   * @param {string} lookupSk - The lookup sort key of the TOC item
   * @returns {number} - The index of the TOC entry in the current list, or -1 if not found
   */
  const getTocItemIdx = (currTocEntries: EnhancedTOCEntry[], lookupPk: string, lookupSk: string): number => {
    return findIndex(currTocEntries, (tocEntry) => isEqual(tocEntry.pk, lookupPk) && isEqual(tocEntry.sk, lookupSk));
  };

  /**
   * Get a new target serial position for the item, based on switching viewType and scheduleType
   * @param currTocEntries - The current TOC entries to use in generating a position
   * @param targetViewType - The target view type ('active' or 'inactive')
   * @param targetScheduleType - The target schedule type (optional for 'inactive' view)
   */
  const getItemTargetFilteredPosition = (
    currTocEntries: EnhancedTOCEntry[],
    targetViewType: TOCViewType,
    targetScheduleType?: ScheduleType,
  ) => {
    // Consider target filtered collection and adjust sort_no and page_no to sync
    const targetFilteredCollection = currTocEntries.filter((tocEntry) => {
      if (targetViewType === 'active') {
        return tocEntry.is_active && tocEntry.schedule_type === targetScheduleType;
      }
      return !tocEntry.is_active;
    });

    // Return a new sort position for the item based on the target filtered view
    return targetFilteredCollection.length > 0 ? getNextSerialNumber(targetFilteredCollection) : 0;
  };

  /**
   * Get all TOC items grouped by the specified group key
   *
   * @param {string} groupKey - The optional group key to filter the TOC entries by. Default is to return all entries.
   * @returns {Map<string, EnhancedTOCEntry[]} - The list of TOC entries grouped by the specified group key, or all entries if no group key is provided
   */
  const getTocItemsByGroup = useCallback(
    (groupKey?: string): Map<string, EnhancedTOCEntry[]> => {
      return groupKey ? new Map([[groupKey, tocEntriesByGroup.get(groupKey) || []]]) : tocEntriesByGroup;
    },
    [tocEntriesByGroup],
  );

  // For our Save Deck operation
  const saveDeckMutation = useSaveDeck();

  /**
   * Check if there are any unsaved changes
   *
   * Determines whether any modifications have been made to the TOC entries
   * by checking for active editing sessions or entries with modification flags.
   * This includes both modified existing entries and newly created entries.
   *
   * @returns {boolean} True if there are unsaved changes, false otherwise
   */
  const hasUnsavedChanges = useCallback(() => {
    // Check for entries marked as modified/new
    return modifiedTocEntries.some((entry) => entry.metadata.isModified || entry.metadata.isNewRow);
  }, [modifiedTocEntries]);

  // Clear data and force refetch when Deck ID (not view changes, such as from schedule type filtering)
  useEffect(() => {
    if (selectedDeckId) {
      resetTocData();
      refetchActive().catch(console.error);
    }
    // NOTE*: Do not include scheduleType here as we only need a refetch of api data on change in DeckId selected
  }, [refetchActive, resetTocData, selectedDeckId]);

  // Initialize entries when tocEntriesData changes (only if not already initialized)
  useEffect(() => {
    if (isTocEntriesFetched && tocEntriesData && tocEntriesData.length > 0 && modifiedTocEntries.length === 0) {
      const tocEntries: EnhancedTOCEntry[] = [];
      const seenTocIds = new Set<string>(); // Used to track unique toc_ids across items
      const deletableEntries: EnhancedTOCEntry[] = []; // Used to track the items we should clear from backend (e.g. any bad data)

      // Add common metadata to our list of TOC entries to aid in frontend manipulations
      forEach(tocEntriesData, (rawTocEntry) => {
        // Ensure every item has a toc_id to guarantee uniqueness (as page_id can be duplicated)
        const parsedTocId = parseIdFromKey(rawTocEntry.sk);

        // Skip processing of the item if we have already seen this toc_id
        // Add item ids to be tracked, as the backend may have duplicates with the same toc_id but different serial_no
        if (seenTocIds.has(parsedTocId)) {
          // Add the item to the deletion collection, and include some debugging output
          deletableEntries.push(rawTocEntry as EnhancedTOCEntry);
          console.debug(
            `Duplicate toc_id "${parsedTocId}" found in data, skipping duplicate entry with sk: ${rawTocEntry.sk}`,
          );
          return;
        }
        // Add any new toc_id to the tracking set
        seenTocIds.add(parsedTocId);

        tocEntries.push(
          initializeMetadata(
            { ...rawTocEntry, toc_id: parsedTocId },
            { sortPos: rawTocEntry.serial_no || getNextSerialNumber(tocEntries) },
          ),
        );
      });

      if (!isEmpty(deletableEntries)) console.debug('Duplicate items found for deletion: ', deletableEntries);

      // Sort the TOC entries we receive by Serial No, as this corresponds to what the User expects to see
      const sortedTocEntriesData = sortBy(tocEntries, (tocEntry) => tocEntry.serial_no);

      // Initialize both original and modified entries with the data from the backend
      setOriginalTocEntries([...sortedTocEntriesData]);
      setModifiedTocEntries([...sortedTocEntriesData]);
      // Mark initial data as loaded since we have data
      setHasInitialDataLoaded(true);
    } else if (isTocEntriesFetched && tocEntriesData && tocEntriesData.length === 0) {
      // Reset core TOC data
      resetTocData();
      // Mark initial data loaded even when the returned entries list is empty
      setHasInitialDataLoaded(true);
    }
  }, [isTocEntriesFetched, modifiedTocEntries.length, resetTocData, tocEntriesData]);

  /**
   * Update Grouped Map of All TOC Items when modifiedTOCEntries is updated
   */
  useEffect(() => {
    const newTocEntriesByGroup = new Map<string, EnhancedTOCEntry[]>();
    forEach(modifiedTocEntries, (tocEntry) => {
      const currActiveState = tocEntry.is_active ? 'active' : 'inactive';
      const currGroupName =
        currActiveState === 'active'
          ? `${currActiveState}-${tocEntry.schedule_type?.toLowerCase() || ScheduleType.MODIFIED.toLowerCase()}` // Treats any missing schedule type entries as modified in the dropdown
          : currActiveState;

      const mapGroup = newTocEntriesByGroup.get(currGroupName) || [];
      mapGroup.push(tocEntry);
      newTocEntriesByGroup.set(currGroupName, mapGroup);
    });
    // Sort and persist the group map
    setTocEntriesByGroup(new Map([...newTocEntriesByGroup.entries()].sort()));
  }, [modifiedTocEntries]);

  // Filter entries based on current view
  const filteredTocEntries = useMemo(() => {
    return modifiedTocEntries.filter((entry) => {
      if (!entry) return false;
      // Exclude deleted entries from displaying
      if (entry.metadata.isDeleted) return false;

      if (currentView === 'active') {
        return entry.is_active === true && entry.schedule_type === scheduleType;
      }
      return entry.is_active === false;
    });
  }, [modifiedTocEntries, currentView, scheduleType]);

  /**
   * Set the current view
   *
   * Sets the current view type (active or inactive pages).
   *
   * @param {TOCViewType} view - The view type to set
   */
  const setView = useCallback((view: TOCViewType) => {
    setCurrentView(view);
  }, []);

  /**
   * General Update TOC Entry handler
   *
   * Allows updating a TOC entry based on the passed in internalTocId,
   * the field attribute to update, and the new value.
   * Performs duplicate checking for fields that require unique values.
   */
  const updateTocEntry = useCallback(
    <TField extends keyof EnhancedTOCEntry>(
      internalTocId: string,
      field: TField,
      newValue: EnhancedTOCEntry[TField],
    ) => {
      // Fields that must have unique values across all TOC entries
      // page_name: Actual page identifier used for routing and references
      const distinctFields: Array<keyof EnhancedTOCEntry> = ['page_name'];

      setModifiedTocEntries((prevEntries) => {
        const targetTocItemIdx = getItemIdxByInternalId(prevEntries, internalTocId);

        // Validate uniqueness for fields that require distinct values
        if (
          distinctFields.includes(field) &&
          checkDuplicateFieldEntry(prevEntries, field, newValue, prevEntries[targetTocItemIdx])
        ) {
          console.debug(`Unable to update. Duplicate value ('${newValue}') found for field '${field}`);
          toastNotification({
            message: `${newValue} already exists. Skipping update`,
            visible: true,
            type: 'warning',
          });
          return prevEntries; // Don't update
        }
        if (targetTocItemIdx !== -1) {
          const newTocEntries = [...prevEntries];
          // Updates the item, based on the field and newValue received from User interaction
          newTocEntries[targetTocItemIdx] = changeEntryField(newTocEntries[targetTocItemIdx], field, newValue);
          return newTocEntries;
        }
        // No item found for changes to be made
        return prevEntries;
      });
    },
    [toastNotification],
  );

  /**
   * Update TOC page ID
   *
   * Updates the TOC page ID for a specific page in the modifiedTocEntries state, in the case page ID does not exist.
   *
   * @param {string} pk - The partition key of the TOC item
   * @param {string} sk - The sort key of the TOC item
   * @todo Refactor this to generate `page_id` (only if necessary) during API data ingestion.
   */
  const updateTocPageId = useCallback((pk: string, sk: string, newPageId: string) => {
    setModifiedTocEntries((prevEntries) => {
      const currTocEntryPos = getTocItemIdx(prevEntries, pk, sk);
      // Item and index exist, proceed with update
      if (currTocEntryPos !== -1) {
        const newTocEntries = [...prevEntries];
        newTocEntries[currTocEntryPos] = { ...newTocEntries[currTocEntryPos], page_id: newPageId };
        return newTocEntries;
      }
      // No item found for changes to be made
      return prevEntries;
    });
  }, []);

  /**
   * Delete a page
   *
   * Marks a page as deleted in the modifiedTocEntries state.
   * This change can be saved or discarded.
   *
   * @param {string} tocId - The TOC ID of the page to delete
   */
  const deletePage = useCallback((tocId: string) => {
    setModifiedTocEntries((prevEntries) => {
      return prevEntries.flatMap((entry) => {
        if (entry.toc_id === tocId) {
          // Page was pending Creation also, so remove it from the TocEntries as User wants to Delete it
          if (entry.metadata.isNewRow) {
            return []; // FlatMap flattens this to nothing
          }
          // Mark matched Page item for deletion
          return markEntryAsDeleted(entry);
        }

        return entry;
      });
    });
  }, []);

  /**
   * Toggle page visibility
   *
   * Toggles the visibility state of a page in the modifiedTocEntries state.
   *
   * @param {string} tocId - The TOC ID of the page to toggle visibility
   */
  const togglePageVisibility = useCallback((tocId: string) => {
    setModifiedTocEntries((prevEntries) => {
      return prevEntries.map((entry) => {
        if (entry.toc_id === tocId) {
          // Mark matched item with new visibility status
          return changeEntryField(entry, 'is_visible', !entry.is_visible);
        }
        return entry;
      });
    });
  }, []);

  /**
   * Toggle page active status
   *
   * Toggles the active state of a page in the modifiedTocEntries state.
   *
   * @param {string} tocId - The TOC ID of the page to toggle active status
   */
  const togglePageActive = useCallback((tocId: string) => {
    setModifiedTocEntries((prevEntries) => {
      return prevEntries.map((entry) => {
        if (entry.toc_id === tocId) {
          // To remove any group association if we are moving to an Inactive state
          const isMovingToInactive = entry.is_active;
          // Update the entry to toggle active state
          let updatedEntry = changeEntryField(entry, 'is_active', !entry.is_active);
          // Handles the case of moving an item to an inactive stage and view
          if (isMovingToInactive) {
            // Get a new filtered view position for the item
            const newFilteredPosition = getItemTargetFilteredPosition(
              prevEntries,
              isMovingToInactive ? 'inactive' : 'active',
            );
            // Update the entry to sync serial_no, page_no, and sortPos for the item

            // Remove group association if moving to inactive
            updatedEntry = changeMultipleEntryFields(updatedEntry, {
              group_name: '',
              page_no: newFilteredPosition.toString(),
              serial_no: newFilteredPosition,
            });
            // Modify metadata `sortPos` field directly
            updatedEntry.metadata = {
              ...updatedEntry.metadata,
              sortPos: newFilteredPosition,
            };
          }
          // Set the current view based on the new is_active state
          setCurrentView(updatedEntry.is_active ? 'active' : 'inactive');
          return updatedEntry;
        }
        return entry;
      });
    });
  }, []);

  /**
   * Moves a page from inactive to active with a specific schedule type
   *
   * @param {string} tocId - The TOC ID of the page to move to active
   * @param {ScheduleType} targetScheduleType - The schedule type to assign (APPROVED or MODIFIED)
   */
  const moveToActiveWithScheduleType = useCallback((tocId: string, targetScheduleType: ScheduleType) => {
    setModifiedTocEntries((prevEntries) => {
      return prevEntries.map((entry) => {
        if (entry.toc_id === tocId) {
          // Get a new filtered view position for the item
          const newFilteredPosition = getItemTargetFilteredPosition(prevEntries, 'active', targetScheduleType);
          // Sync serial_no, page_no and sortPos for the item
          const updatedEntry = changeMultipleEntryFields(entry, {
            is_active: true,
            schedule_type: targetScheduleType,
            page_no: newFilteredPosition.toString(),
            serial_no: newFilteredPosition,
          });
          // Modify metadata `sortPos` field directly
          updatedEntry.metadata = {
            ...updatedEntry.metadata,
            sortPos: newFilteredPosition,
          };
          return updatedEntry;
        }
        return entry;
      });
    });
    // Set the schedule type and switch to active view
    setScheduleType(targetScheduleType);
    setCurrentView('active');
  }, []);

  /**
   * Creates a copy of an existing page in the inactive pages section.
   */
  const copyPage = useCallback(
    (tocPageName: string, pageName: string, onPageCreated?: (tocId: string) => void) => {
      if (!selectedDeckId) {
        console.debug('Cannot create new page: No deck selected');
        return;
      }

      setModifiedTocEntries((prevEntries) => {
        const newPage = createNewTOCEntry(prevEntries, selectedDeckId, `${tocPageName} (1)`, `${pageName} (1)`);
        if (onPageCreated) {
          setTimeout(() => onPageCreated(newPage.toc_id!), 0);
        }
        return [...prevEntries, newPage];
      });

      setCurrentView('inactive');
    },
    [createNewTOCEntry, selectedDeckId],
  );

  /**
   * Creates a new inactive page in the TOC.
   */
  const createNewPage = useCallback(
    (onPageCreated?: (tocId: string) => void) => {
      if (!selectedDeckId) {
        console.debug('Cannot create new page: No deck selected');
        return;
      }

      setModifiedTocEntries((prevEntries) => {
        const newPage = createNewTOCEntry(prevEntries, selectedDeckId);
        if (onPageCreated) {
          setTimeout(() => onPageCreated(newPage.toc_id!), 0);
        }
        return [...prevEntries, newPage];
      });
    },
    [createNewTOCEntry, selectedDeckId],
  );

  /**
   * Discard all changes
   *
   * Resets the modifiedTocEntries state to match the originalTocEntries state,
   * effectively undoing all modifications made to TOC entries.
   */
  const discardChanges = useCallback(() => {
    // Reset modified entries to original data
    setModifiedTocEntries([...originalTocEntries]);
  }, [originalTocEntries]);

  // TOC loading and error states
  const isLoading = isTocEntriesLoading && !!selectedDeckId;
  const isError = isTocEntriesError && !!selectedDeckId;
  const error = isTocEntriesError ? tocEntriesError : null;

  /**
   * Common save deck operation handler
   */
  const executeSaveDeck = useCallback(
    (
      changeSet: SaveDeckChangeSetType,
      successCallback?: () => void,
      failureCallback?: () => void,
      errorMessage = 'Save Deck Error Has Occurred',
    ) => {
      const saveDeckParams: UseSaveDeckParams = {
        deckId: selectedDeckId || undefined,
        deckName: selectedDeck?.deck_name || undefined,
        change_set: changeSet,
        currentUser: username,
        successCallback: successCallback || (async () => {}),
      };

      saveDeckMutation.mutate(saveDeckParams, {
        onError: (saveError) => {
          setIsSaving(false);
          console.error('Save operation failed:', saveError);
          toastNotification({
            message: errorMessage,
            visible: true,
            type: 'error',
          });
          if (failureCallback) failureCallback();
        },
      });
    },
    [selectedDeckId, selectedDeck?.deck_name, saveDeckMutation, toastNotification, username],
  );

  /**
   * Save all Table of Contents changes
   *
   * Saves all changes made to the TOC entries to the backend.
   * After saving, the originalTocEntries state is updated to match the modifiedTocEntries state.
   *
   * @param {() => void} sucessCallback - Optional callback to execute on a successful save from the calling component
   * @param {() => void} failureCallback - Optional callback to execute on a failed save from the calling component
   */
  const saveChanges = useCallback(
    (successCallback?: () => void, failureCallback?: () => void) => {
      setIsSaving(true);

      // Tracks start time of SaveDeck request for RUM tracking
      const saveStartTime = Date.now();

      const transformedTocEntries = transformFieldsInEntries(modifiedTocEntries, {
        toc_id: (entry) => entry.toc_id || parseIdFromKey(entry.sk!),
      });

      const preparedEntries = prepareEntriesForSaveDeckOperation(transformedTocEntries, []);

      console.debug('Entries to create:', preparedEntries.create);
      console.debug('Entries to update:', preparedEntries.update);
      console.debug('Entries to delete:', preparedEntries.delete);

      // Process the api call only if there are actual changes to save
      if (Object.values(preparedEntries).some((entries) => !isEmpty(entries))) {
        /**
         * Added check for duplicates before final formatting and sending to the backend api
         */
        const crudEntries = [...preparedEntries.create, ...preparedEntries.update, ...preparedEntries.delete];
        const duplicateEntries = crudEntries.filter((entry) =>
          checkDuplicateFieldEntry(crudEntries, ['toc_id'], [entry.toc_id!], entry),
        );

        if (!isNil(duplicateEntries) && !isEmpty(duplicateEntries)) {
          console.error('Duplicate toc_id + serial_no combinations detected before SaveDeck:', {
            crudDuplicates: duplicateEntries.map((duplicateEntry) => ({
              toc_id: duplicateEntry.toc_id,
              metadata: 'metadata' in duplicateEntry ? duplicateEntry.metadata : undefined,
            })),
          });
          setIsSaving(false);
          toastNotification({
            message: 'Duplicate Entries Found During Save Deck. Cancelled Save.',
            visible: true,
            type: 'error',
          });
          if (failureCallback) failureCallback();
          return;
        }
        /** End duplication final check */

        // Send for API handling of SaveDeck
        try {
          const validatedTocChangeSet: SaveDeckChangeSetType = SaveDeckChangeSetSchema.parse({
            [ChangeSetTargets.toc]: preparedEntries,
          });

          executeSaveDeck(
            validatedTocChangeSet,
            async () => {
              // Record successful save operation
              rumLogger?.recordApiLatency('DeckManagement', 'SaveDeck', saveStartTime, true);

              // Generate Audit History Trail and Save Audit History
              const auditTrail = generatePageLibraryAuditTrail(
                originalTocEntries,
                preparedEntries.create,
                preparedEntries.update,
                preparedEntries.delete,
              );

              // Persist the generated Audit Trail
              saveAuditTrail(auditTrail);

              // Clear local state to force refresh from backend
              resetTocData();
              setIsSaving(false);

              // Call the prop successCallback
              if (successCallback) successCallback();
            },
            failureCallback,
          );
        } catch (validationError) {
          console.error('SaveDeck changeset validation failed', validationError);
          setIsSaving(false);
          if (failureCallback) failureCallback();
        }
      } else {
        // No changes to save
        setIsSaving(false);
        toastNotification({
          message: 'No Changes to Save',
          visible: true,
          type: 'info',
        });
      }
    },
    [
      modifiedTocEntries,
      toastNotification,
      executeSaveDeck,
      rumLogger,
      generatePageLibraryAuditTrail,
      originalTocEntries,
      saveAuditTrail,
      resetTocData,
    ],
  );

  /**
   * Save Column Template changes
   *
   * Saves all changes made to the column template to the backend.
   *
   * @param {{ create: DeckColumnItemsInput[], update: DeckColumnItemsInput[], delete: DeckColumnDeleteItemsInput[] }} deckColumnItems - The column items to save (create, update, and any delete items)
   * @param {boolean} isUpdate - Whether this is an update operation
   * @param {() => void} successCallback - Optional callback to execute on a successful save
   * @param {() => void} failureCallback - Optional callback to execute on a failed save
   */
  const saveTemplate = useCallback(
    (
      deckColumnItems: {
        create: DeckColumnItemsInput[];
        update: DeckColumnItemsInput[];
        delete: DeckColumnDeleteItemsInput[];
      },
      isUpdate: boolean = false,
      successCallback?: () => void,
      failureCallback?: () => void,
    ) => {
      setIsSaving(true);
      const finalDeckColumnChangeSet = {
        [ChangeSetTargets.deckcolumn]: {},
      };

      if (!isUpdate) {
        // Perform Create action
        finalDeckColumnChangeSet[ChangeSetTargets.deckcolumn] = {
          create: deckColumnItems.create,
        };
      } else {
        // Perform Update action
        finalDeckColumnChangeSet[ChangeSetTargets.deckcolumn] = {
          create: deckColumnItems.create,
          update: deckColumnItems.update,
          delete: deckColumnItems.delete,
        };
      }

      try {
        const validatedColumnChangeSet: SaveDeckChangeSetType = SaveDeckChangeSetSchema.parse(finalDeckColumnChangeSet);
        // Perform api call to Save Deck
        executeSaveDeck(
          validatedColumnChangeSet,
          async () => {
            setIsSaving(false);
            // Call the prop successCallback
            if (successCallback) successCallback();
          },
          failureCallback,
          'Save Deck Error Has Occurred during save template',
        );
      } catch (validationError) {
        console.error('SaveDeck changeset validation failed', validationError);
        // Reset saving state
        setIsSaving(false);
        // Call the prop failureCallback
        if (failureCallback) failureCallback();
      }
    },
    [executeSaveDeck],
  );

  /**
   * Create the context value object
   *
   * Combines all state variables and methods into a single object that will be
   * provided to consumers of the context. This ensures that the context value
   * only changes when one of its dependencies changes, preventing unnecessary
   * re-renders of components that consume the context.
   */
  const contextValue = useMemo<TOCContextType>(
    () => ({
      tocEntries: filteredTocEntries,
      currentView,
      isLoading,
      isError,
      error: error as Error | null,
      hasInitialDataLoaded,
      setView,
      getTocItemIdx,
      getTocItemsByGroup,
      updateTocEntry,
      updateTocPageId,
      deletePage,
      togglePageVisibility,
      togglePageActive,
      moveToActiveWithScheduleType,
      copyPage,
      createNewPage,
      saveChanges,
      discardChanges,
      hasUnsavedChanges,
      scheduleType,
      setScheduleType,
      isSaving,
      deckColumnsForDeck,
      isGetDeckColumnForDeckLoading,
      pageIdToTemplateMap,
      saveTemplate,
    }),
    [
      filteredTocEntries,
      currentView,
      isLoading,
      isError,
      error,
      hasInitialDataLoaded,
      setView,
      getTocItemsByGroup,
      updateTocEntry,
      updateTocPageId,
      deletePage,
      togglePageVisibility,
      togglePageActive,
      moveToActiveWithScheduleType,
      copyPage,
      createNewPage,
      saveChanges,
      discardChanges,
      hasUnsavedChanges,
      scheduleType,
      isSaving,
      deckColumnsForDeck,
      isGetDeckColumnForDeckLoading,
      pageIdToTemplateMap,
      saveTemplate,
    ],
  );

  return <TOCContext value={contextValue}>{children}</TOCContext>;
};

/**
 * Custom hook to access the TOC context
 *
 * @throws {Error} If used outside TOCContextProvider
 * @returns {TOCContextType} The TOC context value
 */
export const useTOCContext = (): TOCContextType => use(TOCContext);
